import React, {useEffect, useRef, useState} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {useIntl} from 'react-intl';
import {
	Button,
	FlexColumn,
	FlexRow,
	Label,
	Modal,
	Pill,
	PillVariant,
	Text,
	TextArea,
	TextField,
} from '@forecast-it/design-system';
import {AgentFormValues, DEFAULT_AGENT_FORM, validateAgentForm} from '../shared/novaAgentForm';
import {NovaModel} from '../shared/novaModelList';
import {NovaModelPicker} from '../shell/NovaModelPicker';
import {useInlineCategoryCreation} from '../state/useInlineCategoryCreation';

export interface NovaCategoryOption {
	categoryId: string;
	name: string;
}

const SUBMIT_BUTTON_TEST_ID = 'nova-create-agent-submit';

export interface CreateAgentModalProps {
	categories: NovaCategoryOption[];
	existingNames: string[];
	models: NovaModel[];
	defaultModelId: string | null;
	onSubmit: (values: AgentFormValues) => Promise<void>;
	onCreateCategory: (name: string) => Promise<NovaCategoryOption>;
	onClose: () => void;
}

// Pill renders no click handler and accepts no data-cy (it's a purely decorative status/tag
// element), and the design system's selection controls don't fit either: ComboBox keeps its
// options hidden until the field is opened (the category chips must be visible immediately), and
// SegmentedControl/ToggleGroup are built for a small fixed set of tabs, not a dynamic,
// user-extensible list of per-item, individually-addressable chips. This unstyled button is the
// minimal wrapper needed to make a Pill clickable and individually selectable; it adds no visual
// styling of its own beyond resetting default button chrome.
const ChipButton = styled.button`
	all: unset;
	display: inline-flex;
	cursor: pointer;
`;

// The submit button is intentionally never natively `disabled` (see handleSubmit below), so DS
// Button's `:disabled` visual treatment never applies. This restores a minimal equivalent look for
// its `aria-disabled` state so an incomplete form doesn't appear fully active.
const SubmitButtonAriaDisabledStyle = createGlobalStyle`
	[data-cy="${SUBMIT_BUTTON_TEST_ID}"][aria-disabled='true'] {
		opacity: 0.5;
		cursor: default;
	}
`;

export function CreateAgentModal({
	categories,
	existingNames,
	models,
	defaultModelId,
	onSubmit,
	onCreateCategory,
	onClose,
}: CreateAgentModalProps): JSX.Element {
	const {formatMessage} = useIntl();
	// Seeded with the platform default so a new agent starts on the model the service would pick
	// anyway, and the author sees which one that is before they save.
	const [values, setValues] = useState<AgentFormValues>({...DEFAULT_AGENT_FORM, model: defaultModelId});
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitFailed, setSubmitFailed] = useState(false);
	// On a successful submit the parent closes (unmounts) this modal synchronously, so guard the
	// post-await state writes to avoid a React "setState on an unmounted component" warning.
	const isMountedRef = useRef(true);
	useEffect(
		() => () => {
			isMountedRef.current = false;
		},
		[]
	);

	const {isValid, missing, duplicateName} = validateAgentForm(values, existingNames);

	const isFieldInvalid = (field: keyof AgentFormValues): boolean => submitAttempted && missing.includes(field);
	const requiredFieldMessage = formatMessage({
		id: 'nova.createAgent.fieldRequiredError',
	});

	const updateField = <K extends keyof AgentFormValues>(field: K, fieldValue: AgentFormValues[K]): void => {
		setValues(previous => ({...previous, [field]: fieldValue}));
	};

	const categoryCreation = useInlineCategoryCreation({
		categoryOptions: categories,
		onCreateCategory,
		onCategoryCreated: created => updateField('categoryId', created.categoryId),
	});

	// The submit button is never natively `disabled` (a native `disabled`
	// button fires no click event in real browsers, so a click on it would
	// never reach this handler and "submit attempted" could never be
	// flipped). Instead the button stays clickable and is only marked
	// `aria-disabled` while the form is incomplete or a submit is already
	// in flight; clicking it in that state reveals the missing-field
	// indicators (or is a no-op while in flight) without double-submitting.
	const handleSubmit = async (): Promise<void> => {
		if (!isValid || submitting) {
			setSubmitAttempted(true);
			return;
		}
		setSubmitting(true);
		setSubmitFailed(false);
		try {
			await onSubmit(values);
		} catch {
			if (isMountedRef.current) {
				setSubmitFailed(true);
			}
		} finally {
			if (isMountedRef.current) {
				setSubmitting(false);
			}
		}
	};

	return (
		<Modal size={Modal.SIZE.LARGE} onCloseModal={onClose} closeOnInteractOutside={false}>
			<SubmitButtonAriaDisabledStyle />
			<Modal.Title>{formatMessage({id: 'nova.createAgent.title.create'})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					{/* `flex={0}` on the field wrappers: FlexColumn defaults to a growing `flex: 1`, which
					    in this height-capped column stretched each wrapper away from the bare TextField
					    and TextArea siblings around it. A field wrapper should be as tall as its field. */}
					<FlexColumn gap={'xs'} flex={0}>
						<TextField
							label={formatMessage({id: 'nova.createAgent.nameLabel'})}
							data-cy="nova-agent-name"
							value={values.name}
							onValueChange={value => updateField('name', value)}
							placeholder={formatMessage({
								id: 'nova.createAgent.namePlaceholder',
							})}
							size={TextField.SIZE.LARGE}
							width={TextField.WIDTH.FLEX}
							hasError={isFieldInvalid('name')}
							errorText={isFieldInvalid('name') ? requiredFieldMessage : undefined}
						/>
						{duplicateName && (
							<Text color="error" data-cy="nova-duplicate-name-warning">
								{formatMessage({
									id: 'nova.createAgent.duplicateNameWarning',
								})}
							</Text>
						)}
					</FlexColumn>

					<FlexColumn gap={'xs'} flex={0}>
						{/* The chip group is hand-rolled, so it has no design-system input label of its
						    own — the required marker is the design-system Label's `*` rather than
						    hand-rolled hint typography beside it. */}
						<Label required data-cy="nova-agent-category-required">
							{formatMessage({id: 'nova.createAgent.categoryLabel'})}
						</Label>
						<div role="group">
							<FlexRow wrap gap={'s'} data-cy="nova-agent-category">
								{categoryCreation.allCategories.map(category => (
									<ChipButton
										key={category.categoryId}
										type="button"
										data-cy={`nova-category-${category.categoryId}`}
										onClick={() => updateField('categoryId', category.categoryId)}
									>
										<Pill
											variant={
												values.categoryId === category.categoryId
													? PillVariant.HIGHLIGHT
													: PillVariant.NEUTRAL
											}
										>
											{category.name}
										</Pill>
									</ChipButton>
								))}
								{!categoryCreation.isAdding && (
									<Button
										emphasis="low"
										icon="plus"
										data-cy="nova-add-category"
										onPress={categoryCreation.startAdding}
									>
										{formatMessage({
											id: 'nova.createAgent.newCategoryPrompt',
										})}
									</Button>
								)}
							</FlexRow>
						</div>
						{isFieldInvalid('categoryId') && (
							<Text color="error" data-cy="nova-agent-category-error">
								{requiredFieldMessage}
							</Text>
						)}
						{categoryCreation.isAdding && (
							<div onKeyDown={categoryCreation.handleKeyDown}>
								<FlexRow gap={'s'} alignItems={'center'}>
									<TextField
										data-cy="nova-new-category-name"
										value={categoryCreation.name}
										onValueChange={categoryCreation.setName}
										placeholder={formatMessage({
											id: 'nova.createAgent.newCategoryPlaceholder',
										})}
										size={TextField.SIZE.LARGE}
										width={TextField.WIDTH.FLEX}
									/>
									<Button
										emphasis="low"
										data-cy="nova-confirm-new-category"
										disabled={
											categoryCreation.isCreating ||
											!categoryCreation.name.trim() ||
											categoryCreation.isDuplicate
										}
										onPress={() => void categoryCreation.confirm()}
									>
										{formatMessage({id: 'nova.createAgent.addCategory'})}
									</Button>
								</FlexRow>
								{categoryCreation.isDuplicate && (
									<Text color="error" data-cy="nova-category-name-taken">
										{formatMessage({id: 'nova.createAgent.categoryNameTaken'})}
									</Text>
								)}
								{categoryCreation.error && (
									<Text color="error" data-cy="nova-create-category-error">
										{formatMessage({
											id: 'nova.createAgent.createCategoryError',
										})}
									</Text>
								)}
							</div>
						)}
					</FlexColumn>

					<TextField
						label={formatMessage({id: 'nova.createAgent.titleLabel'})}
						data-cy="nova-agent-title"
						value={values.title}
						onValueChange={value => updateField('title', value)}
						placeholder={formatMessage({
							id: 'nova.createAgent.titlePlaceholder',
						})}
						size={TextField.SIZE.LARGE}
						width={TextField.WIDTH.FLEX}
						hasError={isFieldInvalid('title')}
						errorText={isFieldInvalid('title') ? requiredFieldMessage : undefined}
					/>

					<NovaModelPicker
						models={models}
						value={values.model}
						onChange={model => updateField('model', model)}
						dataCy="nova-create-agent-model"
					/>

					<TextArea
						label={formatMessage({id: 'nova.createAgent.descriptionLabel'})}
						data-cy="nova-agent-description"
						value={values.description}
						onChange={(value: string) => updateField('description', value)}
						onBlur={() => undefined}
						isOptional={false}
						placeholder={formatMessage({
							id: 'nova.createAgent.descriptionPlaceholder',
						})}
						width={TextArea.WIDTH.FLEX}
						hasError={isFieldInvalid('description')}
						errorText={isFieldInvalid('description') ? requiredFieldMessage : undefined}
					/>

					<TextArea
						label={formatMessage({id: 'nova.createAgent.userPromptLabel'})}
						data-cy="nova-agent-user-prompt"
						value={values.userPromptTemplate}
						onChange={(value: string) => updateField('userPromptTemplate', value)}
						onBlur={() => undefined}
						isOptional={true}
						placeholder={formatMessage({
							id: 'nova.createAgent.userPromptPlaceholder',
						})}
						height={110}
						width={TextArea.WIDTH.FLEX}
					/>

					<TextArea
						label={formatMessage({id: 'nova.createAgent.systemPromptLabel'})}
						data-cy="nova-agent-system-prompt"
						value={values.systemPrompt}
						onChange={(value: string) => updateField('systemPrompt', value)}
						onBlur={() => undefined}
						isOptional={true}
						placeholder={formatMessage({
							id: 'nova.createAgent.systemPromptPlaceholder',
						})}
						height={110}
						width={TextArea.WIDTH.FLEX}
					/>

					{submitFailed && (
						<Text color="error" data-cy="nova-create-agent-submit-failed">
							{formatMessage({
								id: 'nova.createAgent.submitFailed',
							})}
						</Text>
					)}

					<Text color="auxiliary" data-cy="nova-create-agent-footer-note">
						{formatMessage({
							id: 'nova.createAgent.footerNote',
						})}
					</Text>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={() => void handleSubmit()}
					aria-disabled={!isValid || submitting}
					dataCy={SUBMIT_BUTTON_TEST_ID}
				>
					{formatMessage({id: 'nova.createAgent.submitCreate'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onClose} dataCy="nova-create-agent-cancel">
					{formatMessage({id: 'nova.createAgent.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}
